<template>
  <van-popup
    :show="show"
    position="bottom"
    round
    :style="{ height: 'auto' }"
    @update:show="emit('update:show', $event)"
  >
    <div class="pay-qrcode-dialog">
      <div class="dialog-header">
        <span class="dialog-title">扫码支付</span>
        <van-icon name="cross" size="20" @click="close" />
      </div>

      <div class="channel-area" v-if="payChannels.length > 0">
        <van-radio-group v-model="payChannel" direction="horizontal" class="channel-group" @change="onChannelChange">
          <van-radio v-for="channel in payChannels" :key="channel.code" :name="channel.code" :disabled="!channel.enabled">
            {{ channel.name }}
            <span v-if="!channel.enabled && channel.disabledReason" class="channel-reason">{{ channel.disabledReason }}</span>
          </van-radio>
        </van-radio-group>
        <div v-if="!hasEnabledChannel" class="channel-disabled-tip">
          当前订单暂无可用支付渠道，请参考上方说明处理后再发起支付
        </div>
      </div>

      <!-- 博思主扫直付：微信/支付宝切换（切换单流水，旧流水自动作废防双入账） -->
      <div v-if="isBosiDirectPay" class="fuiou-type-area">
        <div
          v-for="opt in fuiouOrderTypes"
          :key="opt.value"
          :class="['fuiou-type-btn', { active: fuiouOrderType === opt.value }]"
          @click="switchFuiouOrderType(opt.value)"
        >{{ opt.label }}</div>
      </div>

      <div class="qrcode-area">
        <div v-if="generating" class="qrcode-loading">
          <van-loading type="spinner" color="#1989fa" size="24" />
          <span>生成中...</span>
        </div>
        <img v-else-if="qrCodeUrl" :src="qrCodeUrl" class="qrcode-img" alt="支付二维码" />
        <div v-else class="qrcode-fail">
          <van-icon name="warning-o" size="32" color="#ee0a24" />
          <span>{{ hasEnabledChannel ? '请点击下方按钮生成支付二维码' : '暂无可生成二维码的支付渠道' }}</span>
        </div>
      </div>

      <div class="pay-amount">
        <span class="amount-label">支付金额</span>
        <span class="amount-value">¥{{ amount }}</span>
      </div>

      <div class="pay-tip">
        <van-icon name="info-o" size="14" />
        <span>{{ payTip }}</span>
      </div>

      <div class="dialog-actions">
        <van-button type="primary" block :loading="generating" :disabled="!hasEnabledChannel" @click="generateQrcode">重新生成</van-button>
        <van-button block @click="close">关闭</van-button>
      </div>
    </div>
  </van-popup>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { showFailToast, showSuccessToast } from 'vant'
import QRCode from 'qrcode'
import {
  addSbPaymentOrder,
  prepareFuiouPay,
  createFuiouPayQrcode,
  closeFuiouPayOrder,
  getFuiouPayStatus,
  listFuiouPayChannels
} from '@/api/order'

const props = defineProps({
  show: { type: Boolean, default: false },
  orderId: { type: [String, Number], default: '' },
  amount: { type: [String, Number], default: 0 },
  companyId: { type: [String, Number], default: '' }
})
const emit = defineEmits(['update:show', 'success'])

const qrCodeUrl = ref('')
const generating = ref(false)
const payChannels = ref([])
const payChannel = ref('')
const statusTimer = ref(null)
const fuiouOrderType = ref('WECHAT')
const fuiouOrderTypes = [
  { label: '微信支付', value: 'WECHAT' },
  { label: '支付宝支付', value: 'ALIPAY' }
]
// 当前富友流水id（主扫渠道切换时作废旧流水用）
let currentFuiouPayId = null

// 博思主扫直付：富友渠道 + 博思订单
const isBosiDirectPay = computed(() => payChannel.value === 'FUIOU' && Number(props.companyId) === 2)
const hasEnabledChannel = computed(() => payChannels.value.some(c => c.enabled))

const payTip = computed(() => {
  if (isBosiDirectPay.value) {
    return '请家长扫码支付（支持微信/支付宝），可长按二维码保存图片转发给家长'
  }
  return '请家长使用微信扫码签署合同并支付，可长按二维码保存图片转发给家长'
})

// 微信公众号 appid（与PC端一致）
const WX_APPID = 'wx0419a515e9e7e37f'
const appDomain = process.env.VUE_APP_DOMAIN || ''

watch(() => props.show, (val) => {
  if (val && props.orderId) {
    initChannels()
  }
  if (!val) {
    clearStatusTimer()
    qrCodeUrl.value = ''
  }
})

onBeforeUnmount(() => {
  clearStatusTimer()
})

async function initChannels() {
  payChannels.value = []
  payChannel.value = ''
  qrCodeUrl.value = ''
  currentFuiouPayId = null
  fuiouOrderType.value = 'WECHAT'
  try {
    const res = await listFuiouPayChannels(props.orderId)
    // 不过滤不可用渠道：保留展示并置灰、附原因说明，与 PC 端口径一致
    payChannels.value = res || []
    const firstEnabled = payChannels.value.find(channel => channel.enabled)
    if (firstEnabled) {
      payChannel.value = firstEnabled.code
    }
  } catch (e) {
    console.error('查询支付渠道失败:', e)
  }
  // 只有一个可用渠道时保持自动生成体验；多渠道或渠道不可用时让员工先选择/处理
  if (payChannels.value.length === 1 && payChannels.value[0].enabled) {
    generateQrcode()
  }
}

/** 渠道切换：作废旧富友流水（防双入账），清空二维码等待重新生成 */
async function onChannelChange() {
  await closeFuiouPayOrderIfAny()
  clearStatusTimer()
  qrCodeUrl.value = ''
}

/** 博思主扫渠道切换（微信/支付宝）：作废旧流水后重新下单生成新渠道二维码 */
async function switchFuiouOrderType(type) {
  if (fuiouOrderType.value === type || generating.value) return
  await closeFuiouPayOrderIfAny()
  clearStatusTimer()
  qrCodeUrl.value = ''
  fuiouOrderType.value = type
  generateQrcode()
}

async function closeFuiouPayOrderIfAny() {
  if (!currentFuiouPayId) return
  try {
    await closeFuiouPayOrder(currentFuiouPayId)
  } catch (e) {
    console.error('作废旧支付流水失败:', e)
  }
  currentFuiouPayId = null
}

async function generateQrcode() {
  if (!hasEnabledChannel.value) {
    showFailToast('暂无可用支付渠道')
    return
  }
  generating.value = true
  qrCodeUrl.value = ''
  clearStatusTimer()
  try {
    if (payChannel.value === 'FUIOU') {
      await generateFuiouQrcode()
    } else if (payChannel.value === 'SB') {
      await generateSbQrcode()
    } else {
      throw new Error('请选择支付渠道')
    }
  } catch (e) {
    console.error('生成支付二维码失败:', e)
    showFailToast('支付二维码生成失败：' + (e?.message || '请重试'))
    qrCodeUrl.value = ''
  } finally {
    generating.value = false
  }
}

async function generateSbQrcode() {
  const res = await addSbPaymentOrder(props.orderId, props.amount)
  const paymentOrderId = res?.paymentOrderId || res
  if (!paymentOrderId) {
    throw new Error('支付单号为空')
  }
  const redirectUri = encodeURIComponent(`${appDomain}/orderDetail/${paymentOrderId}`)
  const finalUrl = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${WX_APPID}&redirect_uri=${redirectUri}&response_type=code&scope=snsapi_base#wechat_redirect`
  qrCodeUrl.value = await QRCode.toDataURL(finalUrl, { width: 240, margin: 2 })
}

async function generateFuiouQrcode() {
  if (Number(props.companyId) === 2) {
    // 博思主扫统一下单：直接返回收款二维码，家长扫码直付（无合同）
    const res = await createFuiouPayQrcode({ orderId: props.orderId, payAmount: Number(props.amount) }, fuiouOrderType.value)
    if (!res?.qrCode) {
      throw new Error('富友收款二维码生成失败')
    }
    currentFuiouPayId = res.payId
    qrCodeUrl.value = await QRCode.toDataURL(res.qrCode, { width: 240, margin: 2 })
    startStatusPolling(res.payId)
  } else {
    // 博尔思防御路径：H5 签约页链接码，家长扫码签署合同后支付
    const res = await prepareFuiouPay({ orderId: props.orderId, payAmount: Number(props.amount) })
    if (!res?.payPath) {
      throw new Error('富友签约页地址为空')
    }
    currentFuiouPayId = res.payId
    qrCodeUrl.value = await QRCode.toDataURL(`${appDomain}${res.payPath}`, { width: 240, margin: 2 })
    startStatusPolling(res.payId)
  }
}

/** 轮询富友支付状态 */
function startStatusPolling(payId) {
  statusTimer.value = window.setInterval(async () => {
    try {
      const statusRes = await getFuiouPayStatus(payId)
      const status = statusRes?.payStatus
      if (status === 'PENDING_SIGN' || status === 'CREATED' || status === 'USERPAYING' || status === 'NOTPAY') {
        return
      }
      clearStatusTimer()
      if (status === 'SUCCESS') {
        showSuccessToast('支付成功')
        emit('success')
        close()
      } else {
        showFailToast('支付未完成，请重新发起')
      }
    } catch (e) {
      console.error('查询富友支付状态失败:', e)
    }
  }, 2000)
}

function clearStatusTimer() {
  if (statusTimer.value) {
    clearInterval(statusTimer.value)
    statusTimer.value = null
  }
}

function close() {
  clearStatusTimer()
  emit('update:show', false)
}
</script>

<style scoped>
.pay-qrcode-dialog {
  padding: 20px 16px calc(20px + env(safe-area-inset-bottom));
}
.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.dialog-title {
  font-size: 17px;
  font-weight: 600;
  color: #323233;
}
.channel-area {
  margin-bottom: 12px;
}
.channel-group {
  justify-content: center;
  gap: 16px;
}
.channel-reason {
  font-size: 12px;
  color: #c8c4cc;
}
.channel-disabled-tip {
  text-align: center;
  font-size: 12px;
  color: #ee0a24;
  margin-top: 8px;
}
.fuiou-type-area {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 12px;
}
.fuiou-type-btn {
  padding: 5px 18px;
  font-size: 13px;
  color: #646566;
  border: 1px solid #dcdee0;
  border-radius: 14px;
}
.fuiou-type-btn.active {
  color: #1989fa;
  border-color: #1989fa;
  background: rgba(25, 137, 250, 0.06);
  font-weight: 600;
}
.qrcode-area {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background: #f7f8fa;
  border-radius: 12px;
  min-height: 260px;
}
.qrcode-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: #969799;
  font-size: 14px;
}
.qrcode-img {
  width: 220px;
  height: 220px;
  border-radius: 8px;
  background: #fff;
  padding: 10px;
}
.qrcode-fail {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #969799;
  font-size: 14px;
}
.pay-amount {
  text-align: center;
  margin: 16px 0 8px;
}
.amount-label {
  font-size: 13px;
  color: #969799;
  margin-right: 8px;
}
.amount-value {
  font-size: 28px;
  font-weight: 700;
  color: #ee0a24;
}
.pay-tip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 12px;
  color: #969799;
  margin-bottom: 16px;
  text-align: center;
}
.dialog-actions {
  display: flex;
  gap: 10px;
}
.dialog-actions .van-button {
  flex: 1;
}
</style>
